import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChatIcon from "@mui/icons-material/Chat";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import { grey } from "@mui/material/colors";
import { useHistory, useParams } from "react-router-dom";
import {
  useBulkDeleteParts,
  useGetPart,
  useStartPartChat,
  useTriggerPartEmbedding,
} from "../services/PartService";
import {
  useGetPartSchema,
  useGetEffectiveFeatures,
} from "../services/PartSchemaService";
import { useListPartGroups } from "../services/PartGroupService";
import { orderFeaturesBySchema } from "../config/partsFeatureDisplay";
import PartMatchCard from "./PartMatchCard";
import EditPartModal from "../components/EditPartModal";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { NucToast } from "src/reusable/components/Toast";
import { extractErrorDetail } from "../utils/errorDetail";

const PART_CHAT_INITIAL_MESSAGE =
  "Help me refine this part's feature sheet. Look over the existing values, flag anything that seems wrong or missing, and suggest improvements.";

const PartDetailView = () => {
  const { partId: partIdParam } = useParams<{ partId: string }>();
  const history = useHistory();

  // A non-numeric URL segment (e.g. /parts/detail/foo) yields NaN. Surface
  // that as a "missing" ID so the query stays disabled and we bounce the
  // user back to the dashboard rather than spinning on "Loading…" forever.
  const parsedPartId = partIdParam ? Number(partIdParam) : NaN;
  const partId =
    Number.isInteger(parsedPartId) && parsedPartId > 0
      ? parsedPartId
      : undefined;

  useEffect(() => {
    if (partIdParam !== undefined && partId === undefined) {
      NucToast({
        message: `Invalid part ID "${partIdParam}".`,
        type: "error",
        hideProgressBar: false,
      });
      history.replace("/parts/catalog");
    }
  }, [partIdParam, partId, history]);

  const {
    data: part,
    isLoading,
    isError,
    isFetching,
    error: partError,
    refetch,
  } = useGetPart(partId);
  const triggerEmbedding = useTriggerPartEmbedding();
  const startPartChat = useStartPartChat();
  const bulkDeleteParts = useBulkDeleteParts();
  const { data: partGroups } = useListPartGroups();

  // Resolve the part's schema directly via its stored part_schema_fk so its
  // features can be shown in schema order with human labels + units.
  const partSchemaFk = part?.part_schema_fk ?? undefined;
  const { data: matchedSchema } = useGetPartSchema(partSchemaFk);
  const { data: effectiveFeatures } = useGetEffectiveFeatures(partSchemaFk);
  const setInitialUserMessage = useChatStore((s) => s.setInitialUserMessage);
  const chatConfigId = useChatStore((s) => s.config?.chatConfigId);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // Latch each one-shot action so the button stays disabled after click —
  // re-embedding spawns background work that takes a while, and starting a
  // chat navigates away; in both cases a second click would be wasted.
  // The latch only resets on page reload.
  const [embedTriggered, setEmbedTriggered] = useState(false);
  const [chatTriggered, setChatTriggered] = useState(false);
  const [compareTriggered, setCompareTriggered] = useState(false);
  const [comparingPartId, setComparingPartId] = useState<number | null>(null);
  const [deleteTriggered, setDeleteTriggered] = useState(false);

  const handleCompare = (matchPartId: number) => {
    if (!part) return;
    setCompareTriggered(true);
    setComparingPartId(matchPartId);
    // Hand off to the comparison splash with both parts pre-selected.
    history.push("/parts/compare", {
      partAId: part.id,
      partBId: matchPartId,
    });
  };

  const isCompareLoading = compareTriggered;

  const handleChatWithPart = async () => {
    if (!part) return;
    setChatTriggered(true);
    try {
      const { run_id: runId, dialogue_id: dialogueId } =
        await startPartChat.mutateAsync({ partId: part.id, chatConfigId });
      // This path skips the splash pages, which are where Parts AI normally
      // resets the document scope at the product boundary. The initial
      // message makes the chat treat this dialogue as a splash hand-off and
      // keep whatever scope is in the store, so drop any scope another
      // product left behind before the first message is sent with it.
      useChatStore.getState().setSourceDataRecordIds([]);
      setInitialUserMessage(PART_CHAT_INITIAL_MESSAGE);
      history.push(`/parts/chat/${runId}/${dialogueId}`);
    } catch (err) {
      const detail = extractErrorDetail(err);
      console.error(`Failed to start part chat: ${detail}`, err);
      NucToast({
        message: `Failed to start chat: ${detail}`,
        type: "error",
        hideProgressBar: false,
      });
      // Release the latch so the user can retry.
      setChatTriggered(false);
    }
  };

  const isChatLoading = startPartChat.isLoading;

  const handleConfirmDelete = async () => {
    if (!part) return;
    setDeleteTriggered(true);
    try {
      const result = await bulkDeleteParts.mutateAsync([part.id]);
      setConfirmDeleteOpen(false);
      if (result.error_count > 0) {
        const firstError = result.errors[0]?.error ?? "unknown error";
        NucToast({
          message: `Failed to delete part: ${firstError}`,
          type: "error",
          hideProgressBar: false,
        });
        // Per-row failure — leave the page, release the latch so the user can retry.
        setDeleteTriggered(false);
        return;
      }
      NucToast({
        message: `Part "${part.name}" deleted.`,
        type: "success",
        hideProgressBar: false,
      });
      history.push("/parts/catalog");
    } catch (err) {
      const detail = extractErrorDetail(err);
      console.error(`Failed to delete part ${part.id}: ${detail}`, err);
      NucToast({
        message: `Failed to delete part: ${detail}`,
        type: "error",
        hideProgressBar: false,
      });
      // Release the latch so the user can retry.
      setDeleteTriggered(false);
    }
  };

  // URL param failed validation — the redirect effect above is already
  // routing the user back to the dashboard; render nothing for that tick
  // instead of flashing a misleading "Loading…" view.
  if (partId === undefined) {
    return null;
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Loading part details...</Typography>
      </Box>
    );
  }

  if (isError || !part) {
    const detail = isError ? extractErrorDetail(partError) : "Part not found.";
    const handleRetry = async () => {
      const result = await refetch();
      if (result.isError) {
        const retryDetail = extractErrorDetail(result.error);
        console.error(
          `Retry failed for part ${partId}: ${retryDetail}`,
          result.error,
        );
        NucToast({
          message: `Retry failed: ${retryDetail}`,
          type: "error",
          hideProgressBar: false,
        });
      }
    };
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: "auto" }}>
        <Stack spacing={2} alignItems="flex-start">
          <Typography variant="h6" fontWeight={600}>
            Couldn't load this part
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {detail}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push("/parts/catalog")}
            >
              Back to dashboard
            </Button>
            <Button
              variant="contained"
              startIcon={
                isFetching ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={handleRetry}
              disabled={isFetching}
            >
              {isFetching ? "Retrying..." : "Retry"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  // Order features by the matched schema (labels + units, schema position),
  // falling back to the part's persisted feature_order when no schema matches.
  const { schemaFeatures, extraFeatures } = orderFeaturesBySchema(
    part.features,
    part.feature_order,
    effectiveFeatures,
  );
  const hasAnyFeatures = schemaFeatures.length > 0 || extraFeatures.length > 0;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={() => history.push("/parts/catalog")}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={600} sx={{ flex: 1 }}>
          {part.name}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ChatIcon />}
          onClick={handleChatWithPart}
          disabled={isChatLoading || chatTriggered}
        >
          {isChatLoading || chatTriggered
            ? "Starting chat..."
            : "Chat with this part"}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={
            triggerEmbedding.isLoading || embedTriggered ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <RefreshIcon />
            )
          }
          onClick={() => {
            setEmbedTriggered(true);
            triggerEmbedding.mutate(part.id, {
              onSuccess: () => {
                refetch();
                // The endpoint is fire-and-forget — it queues a Celery task
                // and returns immediately, so re-enable the button and let
                // the user know results will land later.
                NucToast({
                  message: part.has_embedding
                    ? "Re-embedding queued. Updated matches will appear once the job completes."
                    : "Embedding queued. Matches will appear once the job completes.",
                  type: "info",
                  hideProgressBar: false,
                });
                setEmbedTriggered(false);
              },
              onError: (err) => {
                const detail = extractErrorDetail(err);
                console.error(
                  `Failed to queue embedding for part ${part.id}: ${detail}`,
                  err,
                );
                NucToast({
                  message: `Failed to queue embedding: ${detail}`,
                  type: "error",
                  hideProgressBar: false,
                });
                // Release the latch so the user can retry.
                setEmbedTriggered(false);
              },
            });
          }}
          disabled={triggerEmbedding.isLoading || embedTriggered}
        >
          {triggerEmbedding.isLoading || embedTriggered
            ? part.has_embedding
              ? "Re-embedding..."
              : "Embedding..."
            : part.has_embedding
              ? "Re-embed"
              : "Embed"}
        </Button>
        <IconButton
          size="small"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label="More actions"
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setEditOpen(true);
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit part
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setConfirmDeleteOpen(true);
            }}
            sx={{ color: "error.main" }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete part
          </MenuItem>
        </Menu>
      </Stack>

      <EditPartModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        part={part}
        partGroups={partGroups ?? []}
      />

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Delete this part?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will soft-delete <strong>{part.name}</strong> and remove its
            equivalency matches. The part will no longer appear in the dashboard
            or as a match for other parts. This action cannot be undone from the
            UI.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            disabled={bulkDeleteParts.isLoading || deleteTriggered}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={bulkDeleteParts.isLoading || deleteTriggered}
            startIcon={
              bulkDeleteParts.isLoading || deleteTriggered ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {bulkDeleteParts.isLoading || deleteTriggered
              ? "Deleting..."
              : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
        {/* Left: Part info + Feature Sheet */}
        <Box sx={{ flex: 2 }}>
          {/* Part Info Card */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" spacing={2}>
              {/* Photo */}
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: grey[100],
                  borderRadius: 1,
                  flexShrink: 0,
                }}
              >
                <SettingsIcon sx={{ fontSize: 48, color: grey[400] }} />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ mb: 1, flexWrap: "wrap" }}
                >
                  {part.part_type && (
                    <Chip
                      label={part.part_type}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {part.manufacturer && (
                    <Chip
                      label={part.manufacturer}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {part.has_embedding && (
                    <Chip label="Embedded" size="small" color="success" />
                  )}
                </Stack>

                {part.material_number && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: part.description ? 0.5 : 0 }}
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight={600}
                      color="text.primary"
                    >
                      Material No.{" "}
                    </Typography>
                    {part.material_number}
                  </Typography>
                )}
                {part.description && (
                  <Typography variant="body1" color="text.secondary">
                    {part.description}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Paper>

          {/* Feature Sheet */}
          {hasAnyFeatures && (
            <>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Feature Sheet
                </Typography>
                {matchedSchema && (
                  <Chip
                    label={`Schema: ${matchedSchema.part_type}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Stack>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Feature</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schemaFeatures.map(({ key, label, value }) => (
                      <TableRow key={key}>
                        <TableCell>{label}</TableCell>
                        <TableCell>{value}</TableCell>
                      </TableRow>
                    ))}
                    {extraFeatures.length > 0 && (
                      <>
                        {schemaFeatures.length > 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={2}
                              sx={{
                                fontWeight: 600,
                                color: "text.secondary",
                                bgcolor: grey[50],
                                fontSize: "0.75rem",
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              Other Features
                            </TableCell>
                          </TableRow>
                        )}
                        {extraFeatures.map(({ key, label, value }) => (
                          <TableRow key={key}>
                            <TableCell>{label}</TableCell>
                            <TableCell>{value}</TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>

        {/* Right: Part Matches */}
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            Similar Parts
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {part.matches.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {part.has_embedding
                ? "No similar parts found yet."
                : "Embed this part to find similarities."}
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {part.matches.map((match) => (
                <PartMatchCard
                  key={match.part_id}
                  match={match}
                  onClick={(id) => history.push(`/parts/detail/${id}`)}
                  onCompare={handleCompare}
                  isCompareLoading={isCompareLoading}
                  comparingPartId={comparingPartId}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default PartDetailView;
